import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useAlert } from 'contexts/AlertContext';
import { Header } from 'components/common/header/Header';
import { Button } from 'components/common/button/Button';
import './MeetingShare.css';

const STORAGE_KEY = 'meeting_create_data';

export const MeetingShare = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { showAlert } = useAlert();
    const [copied, setCopied] = useState(false);

    // location.state에서 생성된 모임 데이터 가져오기
    const createdMeeting = location.state?.meeting;

    // sessionStorage에서 입력한 데이터 가져오기
    const savedData = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');

    // 날짜 포맷팅 함수
    const formatDate = (date: Date) => {
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const day = date.getDate();
        const weekdays = ['일', '월', '화', '수', '목', '금', '토'];
        const weekday = weekdays[date.getDay()];
        return `${year}년 ${month}월 ${day}일 (${weekday})`;
    };

    const meetingData = {
        name: savedData.meetingName || createdMeeting?.meetingName || '',
        date: savedData.selectedDate ? formatDate(new Date(savedData.selectedDate)) : '',
        time: savedData.selectedTime || '',
        location: savedData.selectedPlace || savedData.selectedPlaceInfo?.name || '',
        inviteCode: createdMeeting?.inviteCode || 'MO2-XXXXXX'
    };

    // 컴포넌트 언마운트 시 sessionStorage 정리
    useEffect(() => {
        return () => {
            sessionStorage.removeItem(STORAGE_KEY);
        };
    }, []);

    const handleCopyCode = () => {
        navigator.clipboard.writeText(meetingData.inviteCode);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleShareKakao = async () => {
        // Kakao share integration
        await showAlert({ message: '카카오톡으로 공유하기' });
    };

    const handleShareSMS = async () => {
        // SMS share
        await showAlert({ message: '문자로 공유하기' });
    };

    const handleShareLink = async () => {
        const shareUrl = `https://mo2.app/join/${meetingData.inviteCode}`;
        navigator.clipboard.writeText(shareUrl);
        await showAlert({ message: '링크가 복사되었습니다!' });
    };

    const handleComplete = () => {
        navigate('/home', { replace: true });
    };

    return (
        <div className="meeting-share-wrapper">
            <Header title="모임 생성 완료" />

            <div className="meeting-share-content">
                {/* Success Icon */}
                <div className="success-icon-container">
                    <div className="success-icon">✓</div>
                </div>

                {/* Title */}
                <div className="meeting-share-title-section">
                    <h1 className="meeting-share-title">모임이 생성되었어요!</h1>
                    <p className="meeting-share-subtitle">
                        친구들을 초대해서 함께 즐거운 시간을 보내세요.
                    </p>
                </div>

                {/* Meeting Summary Card */}
                <div className="meeting-summary-card">
                    <div className="summary-item">
                        <span className="summary-icon">📋</span>
                        <div className="summary-info">
                            <div className="summary-label">모임명</div>
                            <div className="summary-value">{meetingData.name}</div>
                        </div>
                    </div>

                    <div className="summary-item">
                        <span className="summary-icon">📅</span>
                        <div className="summary-info">
                            <div className="summary-label">날짜</div>
                            <div className="summary-value">{meetingData.date}</div>
                        </div>
                    </div>

                    <div className="summary-item">
                        <span className="summary-icon">⏰</span>
                        <div className="summary-info">
                            <div className="summary-label">시간</div>
                            <div className="summary-value">{meetingData.time}</div>
                        </div>
                    </div>

                    <div className="summary-item">
                        <span className="summary-icon">📍</span>
                        <div className="summary-info">
                            <div className="summary-label">장소</div>
                            <div className="summary-value">{meetingData.location}</div>
                        </div>
                    </div>
                </div>

                {/* Invite Code Section */}
                <div className="invite-code-section">
                    <div className="invite-code-label">초대 코드</div>
                    <div className="invite-code-container">
                        <div className="invite-code-value">{meetingData.inviteCode}</div>
                        <button
                            className={`copy-button ${copied ? 'copied' : ''}`}
                            onClick={handleCopyCode}
                        >
                            {copied ? '복사됨 ✓' : '복사'}
                        </button>
                    </div>
                </div>

                {/* Share Options */}
                <div className="share-options-section">
                    <div className="share-options-label">공유하기</div>
                    <div className="share-buttons-grid">
                        <button className="share-button kakao" onClick={handleShareKakao}>
                            <div className="share-button-icon">💬</div>
                            <div className="share-button-text">카카오톡</div>
                        </button>

                        <button className="share-button sms" onClick={handleShareSMS}>
                            <div className="share-button-icon">💌</div>
                            <div className="share-button-text">문자</div>
                        </button>

                        <button className="share-button link" onClick={handleShareLink}>
                            <div className="share-button-icon">🔗</div>
                            <div className="share-button-text">링크 복사</div>
                        </button>
                    </div>
                </div>
            </div>

            {/* Complete Button */}
            <div className="meeting-share-footer">
                <Button text="완료" onPress={handleComplete} />
            </div>
        </div>
    );
};
